import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { makeStyles, Theme, useTheme } from '@lifeomic/chroma-react/styles';
import moment from 'moment';
import Plot from 'react-plotly.js';
import { Collapse } from '@mui/material';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { Box } from '@lifeomic/chroma-react/components/Box';
import { FilterTimes } from './types';
import { PlotSelectionEvent } from 'plotly.js';

export const useStyles = makeStyles((theme: Theme) => ({
  plot: {
    width: '100%',
    height: 200,
  },
  plotContainer: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    paddingBottom: theme.spacing(1.5),
    marginTop: theme.spacing(1),
  },
}));

const messages = defineMessages({
  helpMessage: {
    id: 'pages.auditLog.histogramHelpMessage',
    defaultMessage:
      'Click and drag to select an event time range to filter on.',
  },
});

export type HistogramData = {
  date: Array<string>;
  number: Array<string>;
};

interface HistogramProps {
  data: HistogramData | null;
  maxTime: number | null;
  setFilterTimes: (filterTimes: FilterTimes) => void;
}

const handleEvent = (
  event: PlotSelectionEvent,
  setFilterTimes: (filterTimes: FilterTimes) => void,
  extendedDates: Array<string>,
) => {
  // Plotly sometimes picks up garbage events
  if ((event as any)?.selections?.length === 0) return;

  // If the user selects no "bars" on the graph, reset the filter
  if (event.points?.length === 0) {
    setFilterTimes({ minTime: 0, maxTime: Date.now() + 1000 });
    return;
  }
  try {
    const minTime = new Date(
      extendedDates[event.points[0]?.pointIndex],
    ).getTime();
    const maxTime = new Date(
      extendedDates[event.points[event?.points?.length - 1]?.pointIndex + 1],
    ).getTime();
    setFilterTimes({ maxTime, minTime });
  } catch (e) {
    setFilterTimes({ minTime: 0, maxTime: Date.now() + 1000 });
  }
};

const Histogram: React.FC<HistogramProps> = ({
  data,
  maxTime,
  setFilterTimes,
}) => {
  const { formatMessage } = useIntl();
  const classes = useStyles({});
  const extendedDates =
    data?.date && maxTime ? [...data.date, moment(maxTime).format()] : [];
  const theme = useTheme();

  return (
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    <Collapse in={data?.date?.length > 1}>
      <Box className={classes.plotContainer} direction="column">
        <Plot
          className={classes.plot}
          data={[
            {
              type: 'bar',
              x: extendedDates,
              y: data?.number,
              marker: {
                color: theme.palette.primary.main,
              },
            },
          ]}
          useResizeHandler
          layout={{
            dragmode: 'select',
            autosize: true,
            margin: { t: 10, r: 30, l: 50, b: 50 },
            bargap: 0.01 as any, // This is valid for plotly but there is a typeerror where it expects either 0 or 1
            xaxis: {
              tickangle: 0,
            },
          }}
          config={{ displayModeBar: false }}
          onSelected={event => {
            handleEvent(event, setFilterTimes, extendedDates);
          }}
        />
        <Box fullHeight fullWidth justify="center">
          <Text size="caption">{formatMessage(messages.helpMessage)}</Text>
        </Box>
      </Box>
    </Collapse>
  );
};

export default React.memo(Histogram);
